import { FC, useState } from 'react';

import { FileTypes, MediaFile } from '@/api/shared.types.ts';
import { VoucherDetails } from '@/api/voucher/voucher.types.ts';
import Button from '@/components/controls/Button.tsx';
import FullScreenMediaViewer from '@/components/controls/FullScreenMediaViewer.tsx';
import VoucherDetailsActionButtons from '@/components/layout/vouchers/VoucherDetailsActionButtons.tsx';
import VoucherDetailsEditForm from '@/components/layout/vouchers/VoucherDetailsEditForm.tsx';
import VoucherDetailsGrid from '@/components/layout/vouchers/VoucherDetailsGrid.tsx';
import { generateVouchersBatchPdf } from '@/utils/generateVoucherPdf.ts';

type VoucherDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  details: VoucherDetails;
  hasActionRole: boolean;
  isAdmin?: boolean;
  onUpdate?: (voucherBatchId: string, validFor: string) => Promise<void>;
  isUpdating?: boolean;
};

const VoucherDetailsModal: FC<VoucherDetailsModalProps> = ({
  isOpen,
  onClose,
  details,
  hasActionRole,
  isAdmin = false,
  onUpdate,
  isUpdating = false,
}: VoucherDetailsModalProps) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  if (!isOpen) {
    return null;
  }

  const canEdit =
    details.status === 'active' && details.validUntilUnix * 1000 > Date.now();

  const handleGeneratePDF = async (): Promise<void> => {
    const voucherPdfBlob = await generateVouchersBatchPdf([details]);
    const voucherPdfUrl = URL.createObjectURL(voucherPdfBlob);

    const pdfFile: MediaFile = {
      id: crypto.randomUUID(),
      type: FileTypes.PDF,
      url: voucherPdfUrl,
      name: `voucher-${details.id}.pdf`,
    };

    setFiles([pdfFile]);
    setSelectedIndex(0);
  };

  const handleClosePDF = (): void => {
    files.forEach((file) => {
      if (file.type === FileTypes.PDF) {
        URL.revokeObjectURL(file.url);
      }
    });

    setFiles([]);
    setSelectedIndex(null);
  };

  const handleEditToggle = (): void => {
    setIsEditMode((prev) => !prev);
  };

  const handleUpdateSubmit = async (validFor: string): Promise<void> => {
    if (onUpdate) {
      await onUpdate(details.batchId, validFor);
      setIsEditMode(false);
    }
  };

  const handleCancel = (): void => {
    setIsEditMode(false);
  };

  const handleClose = (): void => {
    setIsEditMode(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-49 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative bg-white rounded-lg shadow-lg p-6 w-[calc(100%-2rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-honoursOrange text-xl"
          type="button"
          aria-label="Close"
        >
          <i className="pi pi-times" />
        </button>

        <div className="text-h4 mb-3 font-medium text-gray-700 flex items-center py-2">
          <span>Voucher Details</span>
          {canEdit && !isEditMode ? (
            <Button
              text=""
              buttonType={{ type: 'button' }}
              onClick={handleEditToggle}
              buttonStyle="ml-3 cursor-pointer"
              disabled={isUpdating}
              pointer={<i className="pi pi-pencil text-xl self-center" />}
              hasImage
              tooltipText="Edit"
            />
          ) : null}
        </div>

        {isEditMode ? (
          <VoucherDetailsEditForm
            voucherDetails={details}
            onSubmit={handleUpdateSubmit}
            onCancel={handleCancel}
            isLoading={isUpdating}
            isAdmin={isAdmin}
          />
        ) : (
          <>
            <VoucherDetailsGrid voucherDetails={details} isAdmin={isAdmin} />
            {hasActionRole ? (
              <VoucherDetailsActionButtons onGeneratePDF={handleGeneratePDF} />
            ) : null}
          </>
        )}

        <FullScreenMediaViewer
          files={files}
          currentIndex={selectedIndex}
          onClose={handleClosePDF}
          onNext={() => {}}
          onPrev={() => {}}
          isOpen={selectedIndex !== null}
        />
      </div>
    </div>
  );
};

export default VoucherDetailsModal;
